import { IconChevronDown } from "@tabler/icons-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { ClientOnly } from "remix-utils/client-only";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "#app/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "#app/components/ui/select";
import { useOrgTeam } from "#app/contexts/org-team-context.tsx";
import { getLocalizedDisplayName } from "#app/utils/display-name";
import { cn } from "#app/utils/misc.tsx";
import { useKeycloakClaims, useOptionalUser } from "#app/utils/user";

const PERSONAL_ORG_VALUE = "__personal__";
const NO_TEAM_VALUE = "__none__";

type OrgTeamSelectorProps = {
  className?: string;
  /** Render selects directly (e.g. inside the mobile header panel). */
  inline?: boolean;
  /** Fired after org or team changes — close a parent panel when embedded. */
  onSelectionChange?: () => void;
};

/**
 * Header org/team switcher in the new design: a single two-line trigger that
 * opens a popover with the two selects.
 *
 * Same state as the legacy `OrgTeamSwitcher` (`useOrgTeam`), so switching here
 * still fires ORG_TEAM_SELECTION_CHANGED_EVENT and re-scopes every loader.
 */
function OrgTeamSelectorInner({
  className,
  inline = false,
  onSelectionChange,
}: OrgTeamSelectorProps) {
  const { t, i18n } = useTranslation("common");
  const user = useOptionalUser();
  const keycloakClaims = useKeycloakClaims();
  const {
    currentOrg,
    currentTeam,
    organizations = [],
    teams = [],
    setCurrentOrg,
    setCurrentTeam,
  } = useOrgTeam();

  const handleOrgChange = React.useCallback(
    (orgId: string | null) => {
      if (!orgId || orgId === PERSONAL_ORG_VALUE) {
        setCurrentOrg(null);
      } else {
        setCurrentOrg(organizations.find((o) => o.id === orgId) ?? null);
      }
      // Defer so the Select can finish closing before the parent ⋮ panel unmounts.
      queueMicrotask(() => onSelectionChange?.());
    },
    [organizations, onSelectionChange, setCurrentOrg],
  );

  const handleTeamChange = React.useCallback(
    (teamId: string | null) => {
      if (!teamId || teamId === NO_TEAM_VALUE) {
        setCurrentTeam(null);
      } else {
        setCurrentTeam(teams.find((team) => team.id === teamId) ?? null);
      }
      queueMicrotask(() => onSelectionChange?.());
    },
    [onSelectionChange, setCurrentTeam, teams],
  );

  const localizedUserName = getLocalizedDisplayName(user, i18n.language);
  const personalLabel = localizedUserName
    ? t("orgSwitcher.personal", { name: localizedUserName })
    : t("orgSwitcher.personalShort");

  // Base UI resolves the trigger's label through this map; without it the
  // trigger would render the raw org/team id.
  const orgItems: Record<string, string> = {
    [PERSONAL_ORG_VALUE]: personalLabel,
    ...Object.fromEntries(organizations.map((org) => [org.id, org.name])),
  };
  const teamItems: Record<string, string> = {
    [NO_TEAM_VALUE]: t("orgSwitcher.selectTeam"),
    ...Object.fromEntries(teams.map((team) => [team.id, team.name])),
  };

  const orgDisplayName = currentOrg?.name ?? personalLabel;
  const teamDisplayName = currentTeam?.name ?? t("orgSwitcher.selectTeam");
  const showTeamSelect = Boolean(currentOrg) && teams.length > 0;

  const selectorFields = (
    <>
      <div className={cn("w-full", inline ? "px-3 py-2" : "p-3")}>
        <p className="ml-3.5 inline rounded-t-sm bg-orange-50 dark:bg-orange-950/40 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700 dark:text-orange-300">
          {t("orgSwitcher.organization")}
        </p>

        <Select
          modal={false}
          value={currentOrg?.id ?? PERSONAL_ORG_VALUE}
          items={orgItems}
          onValueChange={(v) => handleOrgChange(v ?? "")}
        >
          <SelectTrigger size="sm" className="h-8 w-full rounded-full text-xs">
            <SelectValue placeholder={t("orgSwitcher.selectOrg")} />
          </SelectTrigger>

          <SelectContent className="z-[60] max-h-72 p-2">
            <SelectItem value={PERSONAL_ORG_VALUE}>{personalLabel}</SelectItem>

            {organizations.length === 0 ? (
              <SelectItem value={NO_TEAM_VALUE} disabled>
                {user && keycloakClaims === null
                  ? t("orgSwitcher.noOrgAccess")
                  : t("orgSwitcher.noOrganizations")}
              </SelectItem>
            ) : (
              organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      {showTeamSelect ? (
        <div className={cn("w-full", inline ? "px-3 py-2" : "p-3")}>
          <p className="ml-3.5 inline rounded-t-sm bg-purple-50 dark:bg-purple-950/40 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700 dark:text-purple-300">
            {t("orgSwitcher.yourTeams")}
          </p>

          <Select
            modal={false}
            value={currentTeam?.id ?? NO_TEAM_VALUE}
            items={teamItems}
            onValueChange={(v) => handleTeamChange(v ?? "")}
          >
            <SelectTrigger
              size="sm"
              className="h-8 w-full rounded-full text-xs"
            >
              <SelectValue placeholder={t("orgSwitcher.selectTeam")} />
            </SelectTrigger>

            <SelectContent className="z-[60] max-h-72 p-2">
              <SelectItem value={NO_TEAM_VALUE}>
                {t("orgSwitcher.selectTeam")}
              </SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
    </>
  );

  if (inline) {
    return (
      <div
        className={cn(
          "flex w-full min-w-64 flex-col divide-y divide-zinc-100 gap-0",
          className,
        )}
      >
        {selectorFields}
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger
        className={cn(
          "flex min-w-54 cursor-pointer items-center justify-between gap-2 border-0 sm:border-t md:border-t-0 md:border-l py-2 pr-2 pl-3 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/60 dark:border-border",
          className,
        )}
      >
        <span className="flex min-w-0 flex-col items-start">
          <span className="text-xs text-muted-foreground">
            {t("orgSwitcher.headerLabel")}
          </span>
          <span className="max-w-48 truncate text-xs font-medium">
            {showTeamSelect
              ? `${orgDisplayName} · ${teamDisplayName}`
              : orgDisplayName}
          </span>
        </span>

        <span
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-4xl bg-secondary text-secondary-foreground"
          aria-hidden
        >
          <IconChevronDown size={18} className="shrink-0" />
        </span>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="end"
        className="mt-1 flex w-full min-w-64 flex-col divide-y divide-zinc-100 dark:divide-zinc-800 gap-0 rounded-xl p-0"
      >
        {selectorFields}
      </PopoverContent>
    </Popover>
  );
}

/** Matches the trigger's footprint so SSR does not shift the header. */
function OrgTeamSelectorFallback({ className }: OrgTeamSelectorProps) {
  return (
    <div
      aria-hidden
      className={cn("min-w-54 border-0 border-l py-2 pr-4 pl-3", className)}
    >
      <span className="block h-4 w-24 rounded bg-zinc-100 dark:bg-zinc-800" />
      <span className="mt-1 block h-4 w-36 rounded bg-zinc-100 dark:bg-zinc-800" />
    </div>
  );
}

/** `useOrgTeam` seeds its initial state from localStorage, so render client-side only. */
export function OrgTeamSelector(props: OrgTeamSelectorProps) {
  return (
    <ClientOnly fallback={<OrgTeamSelectorFallback {...props} />}>
      {() => <OrgTeamSelectorInner {...props} />}
    </ClientOnly>
  );
}

export default OrgTeamSelector;
